/* ZonePicker — sélection de la commune, en tête du parcours passager.
   Propose d'abord la position GPS (seule source qui donne la commune), puis la
   liste des communes en repli. `onSkip` est l'échappatoire : le passager doit
   toujours pouvoir jouer, même sans donner sa position — il ne verra alors que
   les tirages nationaux. */
import { motion } from "framer-motion";
import { MapPin, Locate, AlertTriangle } from "lucide-react";

const GEO_HINTS = {
  error: "Position introuvable — choisissez votre commune ci-dessous.",
  unsupported: "Votre téléphone ne partage pas sa position — choisissez ci-dessous.",
};

export default function ZonePicker({
  zones = [],
  loading = false,
  onPick,
  onUseGeo,
  onSkip,
  geoStatus = "idle",
}) {
  const hint = GEO_HINTS[geoStatus];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-8"
      data-testid="zone-picker"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground">
        Localisation
      </div>
      <h1 className="font-heading text-2xl font-bold leading-tight mt-1.5">
        Où êtes-vous&nbsp;?
      </h1>
      <p className="text-sm text-muted-foreground mt-1.5">
        Pour personnaliser votre expérience GoCab Boost, indiquez votre commune.
      </p>

      {/* Position indisponible (erreur ou non supportée) : le bouton disparaît,
          insister ne sert à rien. Le refus explicite est traité à part, avec un
          message qui invite à autoriser la localisation. */}
      {!hint && geoStatus !== "denied" && (
        <button
          type="button"
          onClick={onUseGeo}
          disabled={geoStatus === "loading"}
          data-testid="zone-picker-geo-btn"
          className="mt-6 w-full h-12 rounded-xl font-medium text-white inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-all active:scale-[0.98]"
          style={{
            background: "var(--color-brand-fill)",
            boxShadow: "var(--shadow-card)",
          }}
        >
          <Locate className={`h-4 w-4 ${geoStatus === "loading" ? "animate-pulse" : ""}`} />
          {geoStatus === "loading" ? "Détection en cours…" : "Utiliser ma position"}
        </button>
      )}

      {geoStatus === "denied" && (
        <div
          data-testid="zone-picker-geo-denied"
          className="mt-6 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3.5"
        >
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-900">
                Localisation refusée
              </div>
              <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                Autorisez la localisation pour découvrir les lots à gagner dans
                votre commune. Activez-la dans les réglages de votre navigateur,
                puis réessayez — ou choisissez votre commune ci-dessous.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onUseGeo}
            data-testid="zone-picker-geo-retry-btn"
            className="mt-3 w-full h-10 rounded-lg text-sm font-medium text-white inline-flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            style={{ background: "var(--color-brand-fill)" }}
          >
            <Locate className="h-3.5 w-3.5" />
            Autoriser ma position
          </button>
        </div>
      )}

      {hint && (
        <p
          data-testid="zone-picker-geo-hint"
          className="mt-6 rounded-xl border border-border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground"
        >
          {hint}
        </p>
      )}
      <button
        type="button"
        onClick={onSkip}
        data-testid="zone-picker-skip"
        className="mt-6 w-full h-11 text-sm text-muted-foreground underline underline-offset-4"
      >
        Continuer sans préciser
      </button>
    </motion.div>
  );
}
